<script setup lang="ts">
const globalPageGroups = useState<any[]>('globalPageGroups')
const slots = useSlots()
const route = useRoute()
const localeRoute = useLocaleRoute()
import { camelCase } from 'lodash-es'
// const getLabelForGlobalPage = useLabelForGlobalPage()

interface internalLink {
  value: {
    id: string
    title: string
    slug: string
    globalType?: string
    group?: { docs: (string | { id: string })[] }
    // programme docs: the detail route is nested under the programme type
    type?: { slug?: string | null } | string | null
  }
}

const props = defineProps<{
  data: Link,
  label?: string,
  breakOnAmpersand?: boolean,
}>()


// `pages/programme/[type]/[slug].vue` lives at /exhibitions-events/:type/:slug,
// so a link to a programme doc needs the slug of its type as well
const programmeTypeSlug = (internalLink: internalLink) => {
  const type = internalLink.value.type
  return typeof type === 'object' && type ? type.slug ?? '' : ''
}

const getRouteForinternalLink = (collectionName: string, internalLink: internalLink) => {
  const getGlobalRoute = (globalType: string) => {
    const globalRoutes: Record<string, any> = {
      'home-page': { name: 'index' },
      'apply-page': { name: 'apply' },
      'news-page': { name: 'news' },
      'programme-page': { name: 'programme' },
      'rooms-page': { name: 'rooms' },
      'people-page': { name: 'people' },
      'newsletter-page': { name: 'newsletter' },
    }
    return globalRoutes[globalType] || { name: 'index' }
  }

  if (collectionName === 'pages') {
    const doc = internalLink.value.group?.docs?.[0]
    const groupId = doc instanceof Object ? (doc as { id: string }).id : doc
    const pageGroup = globalPageGroups.value?.find((group: any) => group?.id === groupId)

    if (groupId && pageGroup) {
      const targetId = internalLink.value.id

      if (pageGroup.main?.id === targetId) {
        return { name: 'slug', params: { slug: pageGroup.main.slug } }
      }

      for (const level of pageGroup.levels ?? []) {
        if (level.main?.id === targetId) {
          return { name: 'slug-path', params: { slug: pageGroup.main.slug, path: [level.main.slug] } }
        }
        const child = level.children?.find((c: any) => c.id === targetId)
        if (child) {
          return { name: 'slug-path', params: { slug: pageGroup.main.slug, path: [level.main.slug, child.slug] } }
        }
      }
    } else {
      return { name: 'slug', params: { slug: internalLink.value.slug } }
    }
  }

  const mapping: Record<string, any> = {
    'programme': {
      name: 'programme-type-slug',
      params: { type: programmeTypeSlug(internalLink), slug: internalLink.value.slug },
    },
    'programmeTypes': { name: 'programme-type', params: { type: internalLink.value.slug } },
    'people': { name: 'people-slug', params: { slug: internalLink.value.slug } },
    'news': { name: 'news-slug', params: { slug: internalLink.value.slug } },
    'rooms': { name: 'rooms-slug', params: { slug: internalLink.value.slug } },
    'globalPages': { name: getGlobalRoute(internalLink.value?.globalType ?? '')?.name },
  }

  return mapping[camelCase(collectionName)] || { name: 'index' }
}

const formatUrl = (input: string | null | undefined) => {
  const isUrl = input ? /^https?:\/\//.test(input) : false
  if (isUrl) {
    try {
      const url = new URL(input!)
      return url.hostname + (url.pathname !== '/' ? url.pathname : '')
    } catch (e) {
      return input
    }
  } else {
    return null
  }
}

const label = computed(() => {
  if (props.label) {
    return props.label
  } else if (props.data.label) {
    return props.data.label
  } else if (formatUrl(props.data.externalLink)) {
    return formatUrl(props.data.externalLink)
  } else if (props?.data?.linkType === 'internalLink' && props?.data?.internalLink) {
    const v = props.data.internalLink.value
    return typeof v !== 'string' ? (v as { title: string }).title : 'Unnamed Link'
  } else {
    return 'Unnamed Link'
  }
})

const htmlLabel = computed(() => {
  return label.value
})

const isPersonLink = computed(() => {
  const link = props.data?.internalLink
  if (props.data?.linkType !== 'internalLink' || !link) return false
  return camelCase(link.relationTo || '') === 'people'
})

const personSlug = computed(() => {
  if (!isPersonLink.value) return null
  const value = props.data.internalLink!.value
  return typeof value === 'string' ? null : (value as { slug?: string })?.slug ?? null
})

// a person without a detail page is not a destination, so a CMS link pointing
// at one renders as plain text -- same rule as `shared/person-link.vue`
const linkablePeopleSlugs = useLinkablePeopleSlugs()

const isUnlinkablePerson = computed(() =>
  isPersonLink.value && (!personSlug.value || !linkablePeopleSlugs.value.has(personSlug.value))
)

// a person linked from anywhere but the people list opens as an overlay on the
// current page rather than navigating (see `composables/use-person-overlay.ts`)
const personLink = usePersonLink(personSlug)

const opensInOverlay = computed(() =>
  isPersonLink.value && !isUnlinkablePerson.value && personLink.opensInOverlay.value
)

const routeObject = computed(() => {
  if (props?.data?.linkType === 'internalLink' && props?.data?.internalLink) {
    const resolved = localeRoute(
      getRouteForinternalLink(props.data.internalLink.relationTo || '', props.data.internalLink as internalLink)
    )
    // an optional block anchor on the target page (see the `hash` field on
    // the CMS link); router.options.ts scrolls it into view on arrival
    const hash = props.data.hash?.replace(/^#/, '')
    return resolved && hash ? { ...resolved, hash: `#${hash}` } : resolved
  } else {
    return null
  }
})

const isCurrent = computed(() => {
  if (!routeObject.value) return false
  const { params } = routeObject.value

  if (params?.slug !== undefined) {
    if (params.slug !== route.params.slug) return false
    const linkSegs = ([] as string[]).concat((params.path as string | string[]) ?? [])
    const routeSegs = ([] as string[]).concat((route.params.path as string | string[]) ?? [])
    return linkSegs.join('/') === routeSegs.join('/')
  }

  return routeObject.value.path === route.path
})

// the home link is a prefix of every path (`/` or `/en`), so it never counts
// as a parent
const isParent = computed(() => {
  const linkPath = routeObject.value?.path
  if (!linkPath || String(routeObject.value?.name ?? '').startsWith('index')) return false
  return route.path.startsWith(linkPath.replace(/\/$/, '') + '/')
})

const getAhrefForExternalLink = (link: Link): string | undefined => {
  const base = link.externalLink ?? undefined
  switch (link.externalLinkType) {
    case 'url':
      return base
    case 'email':
      return base ? `mailto:${base}` : undefined
    case 'tel':
      return base ? `tel:${base}` : undefined
    case 'sms':
      return base ? `sms:${base}` : undefined
    default:
      return base
  }
}
</script>

<template>
  <a
    v-if="opensInOverlay"
    :href="personLink.href.value"
    data-internal
    class="focus:outline-none focus-visible:outline-none"
    @click="personLink.onClick"
  >
    <template
      v-if="slots.default"
    >
      <slot :label="label" />
    </template>

    <span
      v-else
      v-html="htmlLabel"
    />
  </a>

  <nuxt-link
    v-else-if="props?.data?.linkType === 'internalLink' && routeObject && !isUnlinkablePerson"
    :to="routeObject"
    :data-current="isCurrent ? '' : undefined"
    :data-is-parent="isParent ? '' : undefined"
    data-internal
    class="focus:outline-none focus-visible:outline-none"
  >
    <template
      v-if="slots.default"
    >
      <slot :label="label" />
    </template>

    <span
      v-else
      v-html="htmlLabel"
    />
  </nuxt-link>

  <a
    v-else-if="props?.data?.linkType === 'externalLink'"
    :href="getAhrefForExternalLink(props.data)"
    :target="props.data?.externalLinkType ? '_blank' : undefined"
    :rel="props.data?.externalLinkType ? 'noopener noreferrer' : undefined"
    data-external
    class="focus:outline-none focus-visible:outline-none"
  >
    <template
      v-if="slots.default"
    >
      <slot :label="label" />
    </template>

    <span
      v-else
      v-html="htmlLabel"
    />
  </a>

  <span
    v-else-if="isUnlinkablePerson"
  >
    <template
      v-if="slots.default"
    >
      <slot :label="label" />
    </template>

    <span
      v-else
      v-html="htmlLabel"
    />
  </span>
</template>
